import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import css from 'styled-jsx/css';
import { Button, ButtonGroup } from 'reactstrap';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import IconsContext from '../../Context/IconsContext';
import { NoItemsExist } from '../NoDataIndication';

const { className: modalClass, styles: modalStyles } = css.resolve`
  @media (max-width: 767px) {
    .modal-dialog {
      height: 100%;
      width: 100%;
      max-height: 100%;
      max-width: 100%;
      margin: 0;
    }
  }
`;
const { className: contentClass, styles: contentStyles } = css.resolve`
  @media (max-width: 767px) {
    .modal-content {
      height: 100%;
    }
  }
  @media (min-width: 768px) {
    .modal-content {
      border-radius: 1rem;
    }
  }
`;
const { className: modalBodyClass, styles: modalBodyStyles } = css.resolve`
  .modal-body {
    padding: 1rem;
  }
  @media (min-width: 768px) {
    .modal-body {
      padding: 1rem 2rem;
      padding-bottom: 0;
    }
  }
`;
const { className: commandButtonClass, styles: commandButtonStyles } =
  css.resolve`
    button {
      color: var(--text-gray);
      background-color: transparent;
      box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.15);
      transition: box-shadow 0.3s ease-in-out;
    }
    button:hover {
      color: var(--text-gray);
      border: 1px solid var(--brand-primary);
      background-color: transparent;
      box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.3);
    }
  `;

const HeaderContainer = ({ nickname, toggleModal }) => {
  const { commandControlIcon } = useContext(IconsContext);
  return (
    <>
      <button
        className="btn btn-link position-absolute close-modal-x text-muted bg-transparent text-decoration-none"
        onClick={toggleModal}
      >
        &#x2A09;
      </button>
      <div className="d-flex flex-row bd-highlight">
        <h5 className="bd-highlight">
          <span>
            <img
              className="command-and-control-icon bd-highlight"
              src={commandControlIcon}
              alt="command-and-control-icon"
            ></img>
          </span>
          Execute Command{' '}
          <span className="font-weight-light text-muted small ml-1">
            on {nickname}
          </span>
        </h5>
      </div>
      <style jsx>{`
        .close-modal-x {
          right: 3px;
          top: 3px;
          outline: none;
          cursor: pointer;
        }
        .command-and-control-icon {
          max-height: 40px;
          margin-right: 5px;
          margin-bottom: 5px;
        }
      `}</style>
    </>
  );
};
HeaderContainer.propTypes = {
  nickname: PropTypes.string,
  toggleModal: PropTypes.func,
};

const CommandsContainer = ({
  commands,
  error,
  handleJobParameterInput,
  jobParameter,
  onCommandButtonClick,
  selectedButton,
  selectedCommand,
}) => {
  const buttonDisabled = (commandID) => {
    return selectedButton.length &&
      selectedCommand._id &&
      selectedButton !== commandID
      ? true
      : false;
  };

  return (
    <>
      <div className="command-container mb-2 w-100">
        <ButtonGroup vertical className="w-100">
          {commands.map((command) => {
            return (
              <>
                <div className="command-button-container p-1 w-100">
                  <Button
                    key={command._id}
                    outline
                    color={
                      command._id === selectedCommand._id ? 'info' : 'light'
                    }
                    block
                    className={`d-flex btn ${commandButtonClass} justify-content-between`}
                    onClick={() =>
                      command._id ? onCommandButtonClick(command) : null
                    }
                    disabled={buttonDisabled(command._id)}
                  >
                    <span className="text-capitalize">
                      <input
                        type="radio"
                        className="radio-circle mr-3 align-top"
                        checked={command._id === selectedCommand._id}
                      />
                      {command.name}
                    </span>
                  </Button>
                </div>
              </>
            );
          })}
        </ButtonGroup>
        {Array.isArray(selectedCommand.parameters) &&
        selectedCommand.parameters.length ? (
          <form className="form-inline pt-2">
            <div className="form-group">
              <label className="pr-2" htmlFor="jobParameter">
                Parameter:
              </label>
              <input
                id="jobParameter"
                className="form-control form-control-sm"
                name="jobParameter"
                onChange={handleJobParameterInput}
                type="text"
                value={jobParameter}
                disabled={selectedButton.length > 0} // Disable on confirm footer
              />
            </div>
          </form>
        ) : null}
        <div className="row justify-content-center text-danger">{error}</div>
      </div>
      {commandButtonStyles}
      <style jsx>{`
        .radio-circle {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border: 1px solid var(--brand-primary-alert);
          border-radius: 50%;
          outline: none;
        }
        .radio-circle:hover {
          box-shadow: 0 0 5px 0px var(--brand-primary-alert);
        }
        .radio-circle:before {
          content: '';
          display: block;
          width: 100%;
          height: 100%;
          border-radius: 50%;
        }
        .radio-circle:checked:before {
          background: var(--brand-primary-alert);
        }
        .command-container {
          height: 60%;
          overflow-y: initial;
          overflow: -moz-scrollbars-none;
          -ms-overflow-style: none;
        }
        .command-container::-webkit-scrollbar {
          width: 0 !important;
        }
        .command-button-container {
          height: 50px;
        }
        @media (min-height: 568px) and (max-height: 768px) {
          .command-button-container {
            height: 60px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
        @media (max-height: 568px) {
          .command-container {
            height: 55%;
          }
        }
      `}</style>
    </>
  );
};
CommandsContainer.propTypes = {
  commands: PropTypes.array,
  error: PropTypes.string,
  handleJobParameterInput: PropTypes.func,
  jobParameter: PropTypes.string,
  onCommandButtonClick: PropTypes.func,
  selectedButton: PropTypes.string,
  selectedCommand: PropTypes.object,
};

const CreateCommandButton = ({ gtemplateID }) => {
  return (
    <>
      <Link
        to={{
          pathname: `/asset-model/${gtemplateID}`,
          search: '?tab=commands',
        }}
        replace={true}
        className="m-auto text-center"
      >
        <button className="btn modal-button-submit py-0 px-3 three-quarter-text">
          <FontAwesomeIcon
            icon={faPlus}
            className="d-inline-block mr-2 font-weight-bold"
          />
          Create Command
        </button>
      </Link>
    </>
  );
};
CreateCommandButton.propTypes = {
  gtemplateID: PropTypes.string,
};

const InitialFooter = ({
  commands,
  handleExecuteCommand,
  jobParameter,
  selectedCommand,
  toggleModal,
}) => {
  return (
    <div className="footer-button-container d-flex justify-content-end mb-0 position-absolute">
      <button
        className="btn btn-outline-secondary col-sm-2 mr-3"
        onClick={toggleModal}
      >
        Cancel
      </button>
      <button
        className="btn modal-button-submit col-sm-2"
        type="submit"
        onClick={selectedCommand._id ? handleExecuteCommand : null}
        disabled={
          !commands.length ||
          (Array.isArray(selectedCommand.parameters) &&
            selectedCommand.parameters.length &&
            !jobParameter.length)
        }
      >
        Execute
      </button>
      <style jsx>{`
        .footer-button-container {
          width: 100%;
          bottom: 30px;
          right: 50px;
        }
        @media (max-height: 768px) {
          .footer-button-container {
            width: 90%;
            bottom: 20px;
            right: 20px;
          }
        }
      `}</style>
    </div>
  );
};
InitialFooter.propTypes = {
  commands: PropTypes.array,
  handleExecuteCommand: PropTypes.func,
  jobParameter: PropTypes.string,
  selectedCommand: PropTypes.object,
  toggleModal: PropTypes.func,
};

const ConfirmFooter = ({
  executeRequestPending,
  handleCancelCommand,
  handleExecuteCommandConfirm,
}) => {
  return (
    <>
      <p className="text-left description-font-size w-100 align-bottom">
        Are you sure you want to send this Command to this Asset? Please ensure
        this is the Command you want to send.
        <br />
        <span className="font-weight-bold">
          Warning: You can&apos;t undo this action.
        </span>
      </p>
      <div className="footer-button-container d-flex justify-content-end mb-0">
        <button
          className="btn btn-outline-secondary col-sm-2 mr-3"
          onClick={handleCancelCommand}
        >
          Cancel
        </button>
        <button
          className={`btn bg-alert-orange text-white col-sm-4`}
          type="submit"
          onClick={handleExecuteCommandConfirm}
        >
          {executeRequestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
          ) : (
            'Yes, Execute Command'
          )}
        </button>
      </div>
      <style jsx>{`
        .description-font-size {
          font-size: 1rem;
        }
        .footer-button-container {
          width: 100%;
          position: absolute;
          bottom: 30px;
          right: 50px;
        }
        @media (min-height: 568px) and (max-height: 768px) {
          .footer-button-container {
            width: 90%;
            bottom: 20px;
            right: 20px;
          }
        }
        @media (max-height: 568px) {
          .description-font-size {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </>
  );
};
ConfirmFooter.propTypes = {
  executeRequestPending: PropTypes.bool,
  handleCancelCommand: PropTypes.func,
  handleExecuteCommandConfirm: PropTypes.func,
};

export default function ExecuteCommandModalDisplay({
  commands,
  error,
  executeRequestPending,
  gtemplateID,
  handleCancelCommand,
  handleExecuteCommand,
  handleExecuteCommandConfirm,
  handleJobParameterInput,
  isOpen,
  jobParameter,
  nickname,
  onCommandButtonClick,
  selectedButton,
  selectedCommand,
  toggleModal,
}) {
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleModal}
      size="lg"
      centered
      className={`${modalClass}`}
      contentClassName={contentClass}
    >
      <ModalBody className={`${modalBodyClass} h-50 pb-0 three-quarter-text`}>
        <div className="container-fluid">
          <HeaderContainer nickname={nickname} toggleModal={toggleModal} />

          <div className="chrome-commands-container">
            {commands.length ? (
              <CommandsContainer
                commands={commands}
                error={error}
                handleJobParameterInput={handleJobParameterInput}
                jobParameter={jobParameter}
                onCommandButtonClick={onCommandButtonClick}
                selectedButton={selectedButton}
                selectedCommand={selectedCommand}
              />
            ) : (
              <div className="row align-items-center">
                <NoItemsExist
                  text="No commands found on the Asset Model"
                  altMessage={nickname}
                />
                <CreateCommandButton gtemplateID={gtemplateID} />
              </div>
            )}
          </div>

          <div className="chrome-footer-container">
            {selectedButton.length > 0 ? (
              <ConfirmFooter
                handleCancelCommand={handleCancelCommand}
                handleExecuteCommandConfirm={handleExecuteCommandConfirm}
                executeRequestPending={executeRequestPending}
              />
            ) : (
              <InitialFooter
                commands={commands}
                handleCancelCommand={handleCancelCommand}
                handleExecuteCommand={handleExecuteCommand}
                jobParameter={jobParameter}
                selectedCommand={selectedCommand}
                toggleModal={toggleModal}
              />
            )}
          </div>
        </div>
      </ModalBody>
      {modalStyles}
      {contentStyles}
      {modalBodyStyles}
      <style jsx>{`
        .chrome-commands-container {
          overflow-y: auto;
          max-height: 500px;
        }
        .chrome-commands-container::-webkit-scrollbar {
          width: 0 !important;
        }

        .chrome-footer-container {
          margin-bottom: 100px;
        }
      `}</style>
    </Modal>
  );
}
ExecuteCommandModalDisplay.propTypes = {
  commands: PropTypes.array,
  error: PropTypes.string,
  executeRequestPending: PropTypes.bool,
  gtemplateID: PropTypes.string,
  handleCancelCommand: PropTypes.func,
  handleExecuteCommand: PropTypes.func,
  handleExecuteCommandConfirm: PropTypes.func,
  handleJobParameterInput: PropTypes.func,
  isOpen: PropTypes.bool,
  jobParameter: PropTypes.string,
  nickname: PropTypes.string,
  onCommandButtonClick: PropTypes.func,
  toggleModal: PropTypes.func,
  selectedButton: PropTypes.string,
  selectedCommand: PropTypes.object,
};
ExecuteCommandModalDisplay.defaultProps = {
  nickname: 'Unnamed Asset',
  selectedButton: '',
  selectedCommand: {},
  commands: [],
};
